import {Icon,type IconName} from './Icon';
import { useId,type ReactNode,type Ref } from 'react';
import type { To } from 'react-router';
import { Link } from './ScopedLink';
import { ScopeIndicator, type ScopeIndicatorProps } from './ScopeIndicator';
import { StatusBadge, type StatusBadgeProps } from './StatusBadge';

export type PageHeaderAction = Readonly<{
  label: string;
  icon?: IconName;
  id?: string;
  ariaLabel?: string;
}> & (
  | Readonly<{
      kind: 'link';
      to: To;
      state?: unknown;
      onClick?: never;
      disabled?: never;
      ariaControls?: never;
      ariaExpanded?: never;
    }>
  | Readonly<{
      kind: 'button';
      onClick: () => void;
      disabled?: boolean;
      ariaControls?: string;
      ariaExpanded?: boolean;
      to?: never;
    }>
);

export type PageHeaderMetadata = Readonly<{
  label: string;
  value: string | number;
}>;

export type PageHeaderProps = Readonly<{
  title: string;
  headingRef?: Ref<HTMLHeadingElement>;
  context?: ReactNode;
  scope?: ScopeIndicatorProps;
  status?: StatusBadgeProps;
  metadata?: ReadonlyArray<PageHeaderMetadata>;
  primaryAction?: PageHeaderAction | undefined;
  secondaryActions?: ReadonlyArray<PageHeaderAction>;
  secondaryContent?: ReactNode;
}>;

function HeaderAction({ action, priority }: Readonly<{ action: PageHeaderAction; priority: 'primary' | 'secondary' }>) {
  const className = `page-header__action page-header__action--${priority}`;
  const icon=action.icon??(priority==='primary'?primaryActionIcon(action.label):undefined);
  if (action.kind === 'link') {
    return <Link id={action.id} className={className} to={action.to} state={action.state} aria-label={action.ariaLabel}>{icon&&<Icon name={icon}/>}<span>{action.label}</span></Link>;
  }
  return <button
    id={action.id}
    className={className}
    type="button"
    onClick={action.onClick}
    disabled={action.disabled}
    aria-label={action.ariaLabel}
    aria-controls={action.ariaControls}
    aria-expanded={action.ariaExpanded}
  >{icon&&<Icon name={icon}/>}<span>{action.label}</span></button>;
}

/** One icon family and meaning across the shared primary-action slot. */
function primaryActionIcon(label:string):IconName|undefined {
  if(/^(Add|New|Create|Prepare|Record)\b/.test(label))return 'plus';
  if(/^Upload\b/.test(label))return 'cloud-upload';
  if(/^Review\b/.test(label))return 'eye';
  if(/^(Open|Continue)\b/.test(label))return 'arrow-right';
  if(/^Print\b/.test(label))return 'file-text';
  return undefined;
}

export function PageHeader({ title, headingRef, context, scope, status, metadata = [], primaryAction, secondaryActions = [],secondaryContent }: PageHeaderProps) {
  const headingId = useId();
  const hasActions = primaryAction !== undefined || secondaryActions.length > 0 || Boolean(secondaryContent);
  return <header className="page-header" data-layout={!scope&&!context&&!status&&!metadata.length?'compact':undefined} aria-labelledby={headingId}>
    <div className="page-header__content">
      {scope && <ScopeIndicator {...scope} />}
      <div className="page-header__title-row">
        <h1 id={headingId} ref={headingRef} tabIndex={headingRef?-1:undefined}>{title}</h1>
        {status && <StatusBadge {...status} />}
      </div>
      {context && <div className="page-header__context">{context}</div>}
      {metadata.length > 0 && <dl className="page-header__metadata">
        {metadata.map((item) => <div key={item.label}>
          <dt>{item.label}</dt>
          <dd>{item.value}</dd>
        </div>)}
      </dl>}
    </div>
    {hasActions && <div className="page-header__actions">
      {primaryAction && <HeaderAction action={primaryAction} priority="primary" />}
      {secondaryActions.map((action) => <HeaderAction key={action.id??`${action.kind}:${action.label}`} action={action} priority="secondary" />)}
      {secondaryContent}
    </div>}
  </header>;
}
